import { TILE_SIZE, ELEVATION_CLIFF_THRESHOLD, ELEVATION_UPHILL_COST, ELEVATION_DOWNHILL_COST, } from './constants/gameConfig.js';
/**
 * 2D collision grid for movement validation.
 * Each cell is either walkable (false) or solid (true).
 *
 * Shared between server (authoritative movement) and client (prediction).
 */
export class CollisionGrid {
    grid;
    elevation;
    trailData = null;
    width;
    height;
    tileSize;
    constructor(width, height, collisionData, tileSize = TILE_SIZE, elevationData) {
        this.width = width;
        this.height = height;
        this.tileSize = tileSize;
        // Convert tile data to boolean: 0 = walkable, non-zero = solid
        this.grid = new Array(width * height);
        for (let i = 0; i < collisionData.length; i++) {
            this.grid[i] = collisionData[i] !== 0;
        }
        // Elevation per tile (default 0 when absent)
        const total = width * height;
        if (elevationData && elevationData.length >= total) {
            this.elevation = elevationData.slice(0, total);
        }
        else {
            this.elevation = new Array(total).fill(0);
        }
    }
    /** Check if a tile coordinate is solid (non-walkable) */
    isSolid(tileX, tileY) {
        if (tileX < 0 || tileX >= this.width || tileY < 0 || tileY >= this.height) {
            return true; // Out of bounds is solid
        }
        return this.grid[tileY * this.width + tileX];
    }
    /** Check if a tile coordinate is walkable */
    isWalkable(tileX, tileY) {
        return !this.isSolid(tileX, tileY);
    }
    /** Convert pixel position to tile coordinate */
    pixelToTile(pixelX, pixelY) {
        return {
            tileX: Math.floor(pixelX / this.tileSize),
            tileY: Math.floor(pixelY / this.tileSize),
        };
    }
    /**
     * Check if a bounding box (in pixels) overlaps any solid tile.
     * Used for entity collision with configurable entity size.
     * @param x Center X in pixels
     * @param y Center Y in pixels
     * @param halfWidth Half of entity width in pixels
     * @param halfHeight Half of entity height in pixels
     */
    collidesBox(x, y, halfWidth, halfHeight) {
        const left = Math.floor((x - halfWidth) / this.tileSize);
        const right = Math.floor((x + halfWidth - 0.01) / this.tileSize);
        const top = Math.floor((y - halfHeight) / this.tileSize);
        const bottom = Math.floor((y + halfHeight - 0.01) / this.tileSize);
        for (let ty = top; ty <= bottom; ty++) {
            for (let tx = left; tx <= right; tx++) {
                if (this.isSolid(tx, ty)) {
                    return true;
                }
            }
        }
        return false;
    }
    /** Set a tile's solid state at runtime (e.g. tide flooding) */
    setTileSolid(tileX, tileY, solid) {
        if (tileX < 0 || tileX >= this.width || tileY < 0 || tileY >= this.height)
            return;
        this.grid[tileY * this.width + tileX] = solid;
    }
    /** World dimensions in pixels */
    get worldWidth() {
        return this.width * this.tileSize;
    }
    get worldHeight() {
        return this.height * this.tileSize;
    }
    /** Get the elevation value at a tile coordinate. Returns 0 for out-of-bounds. */
    getElevation(tileX, tileY) {
        if (tileX < 0 || tileX >= this.width || tileY < 0 || tileY >= this.height) {
            return 0;
        }
        return this.elevation[tileY * this.width + tileX];
    }
    /** Set the elevation value at a tile coordinate. */
    setElevation(tileX, tileY, value) {
        if (tileX < 0 || tileX >= this.width || tileY < 0 || tileY >= this.height)
            return;
        this.elevation[tileY * this.width + tileX] = value;
    }
    /** Replace the entire elevation array (e.g. after procedural generation). */
    setElevationBulk(data) {
        const total = this.width * this.height;
        for (let i = 0; i < total && i < data.length; i++) {
            this.elevation[i] = data[i];
        }
    }
    /**
     * Like collidesBox but also treats upward cliffs as impassable.
     * One-way cliffs: you can fall/walk down any drop, but you cannot climb
     * up a cliff whose elevation exceeds the source by >= CLIFF_THRESHOLD.
     * @param fromX Current center X in pixels (used to determine source elevation)
     * @param fromY Current center Y in pixels
     * @param x Proposed center X in pixels
     * @param y Proposed center Y in pixels
     * @param halfWidth Half of entity width in pixels
     * @param halfHeight Half of entity height in pixels
     */
    collidesBoxElevation(fromX, fromY, x, y, halfWidth, halfHeight) {
        const srcTile = this.pixelToTile(fromX, fromY);
        const srcElev = this.getElevation(srcTile.tileX, srcTile.tileY);
        const left = Math.floor((x - halfWidth) / this.tileSize);
        const right = Math.floor((x + halfWidth - 0.01) / this.tileSize);
        const top = Math.floor((y - halfHeight) / this.tileSize);
        const bottom = Math.floor((y + halfHeight - 0.01) / this.tileSize);
        for (let ty = top; ty <= bottom; ty++) {
            for (let tx = left; tx <= right; tx++) {
                if (this.isSolid(tx, ty))
                    return true;
                // One-way cliff: block climbing UP, allow falling DOWN
                const destElev = this.getElevation(tx, ty);
                if (destElev - srcElev >= ELEVATION_CLIFF_THRESHOLD)
                    return true;
            }
        }
        return false;
    }
    /**
     * Returns the movement speed multiplier for travelling from one pixel position
     * to another based on their elevation difference.
     * Uphill → ELEVATION_UPHILL_COST, Downhill → ELEVATION_DOWNHILL_COST, Flat → 1.0
     */
    elevationSpeedMultiplier(fromX, fromY, toX, toY) {
        const src = this.pixelToTile(fromX, fromY);
        const dst = this.pixelToTile(toX, toY);
        const diff = this.getElevation(dst.tileX, dst.tileY) - this.getElevation(src.tileX, src.tileY);
        if (diff > 0)
            return 1 / ELEVATION_UPHILL_COST; // slower uphill
        if (diff < 0)
            return 1 / ELEVATION_DOWNHILL_COST; // faster downhill
        return 1;
    }
}
//# sourceMappingURL=CollisionGrid.js.map